
// Queenli Creator Platform
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthProvider, useAuth } from './lib/AuthContext';
import Header from './components/Header';
import Hero from './components/Hero';
import Steps from './components/Steps';
import Compensation from './components/Compensation';
import CreatorProfile from './components/CreatorProfile';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import ApplicationForm from './components/ApplicationForm';
import LoginPage from './components/LoginPage';
import AdminLoginPage from './components/Admin/AdminLoginPage';
import StudioLoginPage from './components/Studio/StudioLoginPage';
import StudioSetupPassword from './components/Studio/StudioSetupPassword';
import ResetPassword from './components/ResetPassword';
import ApplicationTerms from './components/ApplicationTerms';
import PrivacyPolicy from './components/PrivacyPolicy';
import ApplicationSubmitted from './components/ApplicationSubmitted';
import TestUpload from './components/TestUpload';

// Lazy-loaded route containers (code splitting)
const DashboardLayout = React.lazy(() => import('./components/Dashboard/DashboardLayout'));
const AdminLayout = React.lazy(() => import('./components/Admin/AdminLayout'));
const StudioLayout = React.lazy(() => import('./components/Studio/StudioLayout'));
const CreatorOnboarding = React.lazy(() => import('./components/CreatorOnboarding'));
const ScorerResults = React.lazy(() => import('./components/ScorerResults'));
const VideoPlaybackTest = React.lazy(() => import('./components/Admin/VideoPlaybackTest'));

// Loading component
const LoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400">
    <div className="animate-pulse">Loading Queenli...</div>
  </div>
);

// Protected Route for authenticated users (creators)
const DeactivatedScreen: React.FC = () => {
  const { signOut } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-10 max-w-sm w-full text-center">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6">!</div>
        <h2 className="text-xl font-black text-slate-900 mb-2">Account Deactivated</h2>
        <p className="text-slate-500 text-sm mb-8">Your account has been deactivated. Please contact your admin for assistance.</p>
        <button onClick={signOut} className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors">Sign Out</button>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isLoading, isAdmin, isAdminChecking, isTeamMember, isDeactivatedMember, isTeamMemberChecking } = useAuth();
  const location = useLocation();

  // Wait for session AND role checks before rendering anything
  if (isLoading || isAdminChecking || isTeamMemberChecking) return <LoadingScreen />;

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Block deactivated team members entirely
  if (isDeactivatedMember) return <DeactivatedScreen />;

  // If admin tries to access creator dashboard, redirect to admin
  if (isAdmin) {
    return <Navigate to="/manage" replace />;
  }

  // If team member tries to access creator dashboard, redirect to studio
  if (isTeamMember) {
    return <Navigate to="/studio" replace />;
  }

  return <>{children}</>;
};

// Protected Route for admin users
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isLoading, isAdmin, isAdminChecking } = useAuth();
  const location = useLocation();

  // Wait for both initial loading AND admin check to complete
  if (isLoading || isAdminChecking) return <LoadingScreen />;

  if (!session) {
    return <Navigate to="/manage/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Protected Route for team members (video creators)
const StudioRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isLoading, isAdmin, isAdminChecking, isTeamMember, isDeactivatedMember, isTeamMemberChecking } = useAuth();
  const location = useLocation();

  // Wait for all checks to complete
  if (isLoading || isAdminChecking || isTeamMemberChecking) return <LoadingScreen />;

  if (!session) {
    return <Navigate to="/studio/login" state={{ from: location }} replace />;
  }

  // Block deactivated team members entirely
  if (isDeactivatedMember) return <DeactivatedScreen />;

  // Admins should go to admin dashboard
  if (isAdmin) {
    return <Navigate to="/manage" replace />;
  }

  if (!isTeamMember) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public route that redirects authenticated users
const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isLoading, isAdmin, isAdminChecking, isTeamMember, isTeamMemberChecking } = useAuth();

  // Wait for all role checks before deciding where to redirect
  if (isLoading || isAdminChecking || isTeamMemberChecking) return <LoadingScreen />;

  // Check if this is an invite/auth callback (tokens in URL hash)
  // Allow user to proceed to set password even if they have a session
  const isAuthCallback = window.location.hash.includes('access_token') ||
                         window.location.hash.includes('type=invite') ||
                         window.location.hash.includes('type=recovery');

  if (session && !isAuthCallback) {
    const dest = isAdmin ? '/manage' : isTeamMember ? '/studio' : '/dashboard';
    return <Navigate to={dest} replace />;
  }

  return <>{children}</>;
};

// Layout wrapper for public pages with header/footer
const PublicLayout: React.FC<{ children: React.ReactNode; currentView?: string }> = ({ children, currentView = 'landing' }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header currentView={currentView} />
      {children}
      <Footer />
    </div>
  );
};

// Landing Page component
const LandingPage: React.FC = () => {
  return (
    <PublicLayout currentView="landing">
      <main className="flex-grow">
        <Hero />
        <div id="how-it-works">
          <Steps />
        </div>
        <Compensation />
        <CreatorProfile />
        <div id="faq">
          <FAQ />
        </div>
      </main>
    </PublicLayout>
  );
};

// Login Page wrapper
const LoginPageWrapper: React.FC = () => {
  return (
    <PublicLayout currentView="login">
      <main className="flex-grow flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <LoginPage />
      </main>
    </PublicLayout>
  );
};

// Admin Login Page wrapper
const AdminLoginWrapper: React.FC = () => {
  return (
    <PublicLayout currentView="login">
      <main className="flex-grow flex items-center justify-center bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <AdminLoginPage />
      </main>
    </PublicLayout>
  );
};

// Studio Login Page wrapper
const StudioLoginWrapper: React.FC = () => {
  return (
    <PublicLayout currentView="login">
      <main className="flex-grow flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <StudioLoginPage />
      </main>
    </PublicLayout>
  );
};

// Reset Password wrapper
const ResetPasswordWrapper: React.FC = () => {
  return (
    <PublicLayout currentView="login">
      <main className="flex-grow flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <ResetPassword />
      </main>
    </PublicLayout>
  );
};

// Application Form wrapper
const ApplyPageWrapper: React.FC = () => {
  return (
    <PublicLayout currentView="apply">
      <main className="flex-grow bg-gray-50 py-20 pb-40">
        <ApplicationForm />
      </main>
    </PublicLayout>
  );
};

// Application Submitted wrapper
const ApplicationSubmittedWrapper: React.FC = () => {
  return (
    <PublicLayout currentView="apply">
      <main className="flex-grow bg-gray-50 py-20">
        <ApplicationSubmitted />
      </main>
    </PublicLayout>
  );
};

// Terms wrapper
const TermsWrapper: React.FC = () => {
  return (
    <PublicLayout currentView="landing">
      <main className="flex-grow bg-gray-50 py-20">
        <ApplicationTerms />
      </main>
    </PublicLayout>
  );
};

// Privacy wrapper
const PrivacyWrapper: React.FC = () => {
  return (
    <PublicLayout currentView="landing">
      <main className="flex-grow bg-gray-50 py-20">
        <PrivacyPolicy />
      </main>
    </PublicLayout>
  );
};

// Track SPA page views for Meta Pixel
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const MetaPixelPageView: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);

  return null;
};

// Track route changes as Sentry breadcrumbs for error context
const SentryRouteTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${location.pathname}`,
      level: 'info',
    });
  }, [location.pathname]);

  return null;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
    <MetaPixelPageView />
    <SentryRouteTracker />
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/apply" element={<ApplyPageWrapper />} />
      <Route path="/apply/submitted" element={<ApplicationSubmittedWrapper />} />
      <Route path="/terms" element={<TermsWrapper />} />
      <Route path="/privacy" element={<PrivacyWrapper />} />
      <Route path="/t/:token" element={<TestUpload />} />
      <Route path="/onboard/:token" element={<CreatorOnboarding />} />
      <Route path="/reset-password" element={<ResetPasswordWrapper />} />
      <Route path="/scores/:runId" element={<ScorerResults />} />
      <Route path="/test/video-playback" element={<VideoPlaybackTest />} />

      {/* Auth routes - redirect if already logged in */}
      <Route path="/login" element={
        <PublicOnlyRoute>
          <LoginPageWrapper />
        </PublicOnlyRoute>
      } />
      <Route path="/manage/login" element={
        <PublicOnlyRoute>
          <AdminLoginWrapper />
        </PublicOnlyRoute>
      } />

      {/* Protected creator routes */}
      <Route path="/dashboard/*" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      } />

      {/* Studio login */}
      <Route path="/studio/login" element={
        <PublicOnlyRoute>
          <StudioLoginWrapper />
        </PublicOnlyRoute>
      } />

      {/* Studio password setup (invite callback) - no auth guard */}
      <Route path="/studio/setup" element={<StudioSetupPassword />} />

      {/* Protected studio routes */}
      <Route path="/studio/*" element={
        <StudioRoute>
          <StudioLayout />
        </StudioRoute>
      } />

      {/* Protected admin routes */}
      <Route path="/manage/*" element={
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      } />

      {/* Legacy hash redirects for backwards compatibility */}
      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
    </Suspense>
    </>
  );
};

// Handle legacy hash URLs and redirect to new paths
const LegacyRedirect: React.FC = () => {
  const location = useLocation();

  // Check if there's a hash in the URL
  if (location.hash) {
    const hash = location.hash;

    if (hash === '#admin' || hash === '#admin-login') {
      return <Navigate to="/manage/login" replace />;
    }
    if (hash === '#login') {
      return <Navigate to="/login" replace />;
    }
    if (hash.startsWith('#dashboard')) {
      const subPath = hash.replace('#dashboard', '');
      return <Navigate to={`/dashboard${subPath}`} replace />;
    }
    if (hash.startsWith('#admin-dashboard')) {
      const subPath = hash.replace('#admin-dashboard', '');
      return <Navigate to={`/manage${subPath}`} replace />;
    }
    if (hash === '#apply') {
      return <Navigate to="/apply" replace />;
    }
    if (hash === '#apply/submitted') {
      return <Navigate to="/apply/submitted" replace />;
    }
    if (hash === '#terms') {
      return <Navigate to="/terms" replace />;
    }
    if (hash.startsWith('#test-upload/')) {
      const token = hash.replace('#test-upload/', '');
      return <Navigate to={`/t/${token}`} replace />;
    }
    if (hash.includes('recovery') || hash.includes('access_token')) {
      return <Navigate to="/reset-password" replace />;
    }
  }

  // Default: redirect to home
  return <Navigate to="/" replace />;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
